import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Search, Columns3, CalendarDays, CalendarRange, Table2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errMsg, fmtDate, fromDateInput } from "@/lib/api";
import { useMe } from "@/lib/me";
import { STORY_STAGES, stageLabel, FORMATS, formatLabel, SECTIONS, canAccess } from "@/lib/constants";
import { PageHeader, Pill, KanbanBoard, WebflowBadge, Dot, SampleTag, EmptyState, SectionTag, StageTag } from "@/components/app/shared";
import { cn } from "@/lib/utils";

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const startOfWeek = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};

const emptyStory = (authorName) => ({ title: "", section: "Cultura", format: "DIGITAL_FEATURE", authorName, publishDate: "", excerpt: "", printBookEligible: false });

function NewStoryDialog({ open, setOpen }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useMe();
  const [f, setF] = useState(() => emptyStory(me.name));
  useEffect(() => { if (open) setF(emptyStory(me.name)); }, [open, me.name]);
  const create = useMutation({
    mutationFn: async () => (await api.post("/stories", { ...f, publishDate: fromDateInput(f.publishDate) })).data,
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ["stories"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Borrador creado");
      setOpen(false);
      navigate(`/editorial/${s.id}`);
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="new-story-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-4xl font-normal">Nuevo borrador</DialogTitle>
          <DialogDescription>Empieza con lo básico; el resto se completa en el espacio de trabajo.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Título</Label>
            <Input autoFocus value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="new-story-title-input" placeholder="Titular de trabajo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Sección</Label>
              <Select value={f.section} onValueChange={(v) => setF({ ...f, section: v })}>
                <SelectTrigger data-testid="new-story-section-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Formato</Label>
              <Select value={f.format} onValueChange={(v) => setF({ ...f, format: v, printBookEligible: v === "BOOK_ESSAY" || f.printBookEligible })}>
                <SelectTrigger data-testid="new-story-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMATS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Autor/a</Label>
              <Input value={f.authorName} onChange={(e) => setF({ ...f, authorName: e.target.value })} data-testid="new-story-author-input" />
            </div>
            <div className="grid gap-1.5">
              <Label>Fecha de publicación</Label>
              <Input type="date" value={f.publishDate} onChange={(e) => setF({ ...f, publishDate: e.target.value })} data-testid="new-story-date-input" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Resumen</Label>
            <Input value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} placeholder="Una línea que venda la historia" data-testid="new-story-excerpt-input" />
          </div>
          <label className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
            Elegible para el Libro Anual
            <Switch checked={f.printBookEligible} onCheckedChange={(v) => setF({ ...f, printBookEligible: v })} data-testid="new-story-print-switch" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={() => create.mutate()} disabled={!f.title.trim() || create.isPending} data-testid="new-story-create-button">Crear borrador</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const StoryCard = ({ s, compact, onClick }) => (
  <div onClick={onClick} data-testid="story-card" className={cn("group rounded-md border border-border bg-card p-3 transition-colors hover:border-foreground/40", onClick && "cursor-pointer")}>
    <div className="flex items-center justify-between gap-2">
      <SectionTag section={s.section} className="!px-1.5 !py-0 !text-[10px]" />
      <div className="flex items-center gap-1.5">
        {s.isSample && <SampleTag />}
        <Dot tone={s.webflowStatus === "LIVE" ? "ok" : s.webflowStatus === "DRAFT" ? "warn" : "muted"} />
      </div>
    </div>
    <div className={cn("mt-1.5 font-medium leading-snug", compact ? "line-clamp-2 text-[12px]" : "text-[13.5px]")}>{s.title}</div>
    {!compact && (
      <div className="mt-2.5 flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="truncate">{s.authorName || "Sin autor"}</span>
        <span className="font-mono">{fmtDate(s.publishDate)}</span>
      </div>
    )}
    {!compact && s.format !== "DIGITAL_FEATURE" && <Pill tone={s.format === "BOOK_ESSAY" ? "butter" : "neutral"} className="mt-2 text-[10px]">{formatLabel(s.format)}</Pill>}
  </div>
);

function MonthView({ stories, open }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const first = startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  const days = [...Array(42)].map((_, i) => { const d = new Date(first); d.setDate(first.getDate() + i); return d; });
  const today = new Date();
  return (
    <div className="rounded-lg border border-border bg-card" data-testid="calendar-month-view">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="font-display text-3xl capitalize">{cursor.toLocaleDateString("es-SV", { month: "long", year: "numeric" })}</div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="calendar-prev" aria-label="Mes anterior"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }}>Hoy</Button>
          <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="calendar-next" aria-label="Mes siguiente"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-border">{DAYS.map((d) => <div key={d} className="kicker px-2 py-2">{d}</div>)}</div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const list = stories.filter((s) => s.publishDate && sameDay(new Date(s.publishDate), d));
          const out = d.getMonth() !== cursor.getMonth();
          return (
            <div key={i} className={cn("min-h-[112px] border-b border-r border-border p-1.5", out && "bg-surface-2", (i + 1) % 7 === 0 && "border-r-0")}>
              <div className={cn("mb-1 inline-flex h-5 min-w-5 items-center justify-center rounded px-1 font-mono text-[11px]", sameDay(d, today) ? "bg-primary text-primary-foreground" : out ? "text-muted-foreground/60" : "text-muted-foreground")}>{d.getDate()}</div>
              <div className="space-y-1">
                {list.slice(0, 3).map((s) => (
                  <button key={s.id} onClick={() => open(s)} className="block w-full truncate rounded border border-border bg-secondary px-1.5 py-0.5 text-left text-[11px] hover:bg-accent" data-testid="calendar-story">
                    <Dot tone={s.status === "PUBLISHED" ? "ok" : s.status === "READY_WEBFLOW" ? "warn" : "muted"} className="mr-1 align-middle" />{s.title}
                  </button>
                ))}
                {list.length > 3 && <div className="px-1 text-[10px] text-muted-foreground">+{list.length - 3} más</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ stories, open }) {
  const [start, setStart] = useState(() => startOfWeek(new Date()));
  const days = [...Array(7)].map((_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const end = days[6];
  return (
    <div className="rounded-lg border border-border bg-card" data-testid="calendar-week-view">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="font-display text-3xl">{fmtDate(start.toISOString(), { day: "numeric", month: "short" })} — {fmtDate(end.toISOString(), { day: "numeric", month: "short", year: "numeric" })}</div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => { const d = new Date(start); d.setDate(d.getDate() - 7); setStart(d); }} aria-label="Semana anterior" data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setStart(startOfWeek(new Date()))}>Hoy</Button>
          <Button variant="ghost" size="icon" onClick={() => { const d = new Date(start); d.setDate(d.getDate() + 7); setStart(d); }} aria-label="Semana siguiente" data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7">
        {days.map((d, i) => {
          const list = stories.filter((s) => s.publishDate && sameDay(new Date(s.publishDate), d));
          return (
            <div key={i} className={cn("min-h-[300px] border-b border-border p-2 md:border-b-0 md:border-r", i === 6 && "md:border-r-0", sameDay(d, new Date()) && "bg-surface-2")}>
              <div className="mb-2 flex items-baseline justify-between">
                <span className="kicker">{DAYS[i]}</span>
                <span className="font-display text-2xl">{d.getDate()}</span>
              </div>
              <div className="space-y-2">{list.map((s) => <StoryCard key={s.id} s={s} compact onClick={() => open(s)} />)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Editorial() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState(() => localStorage.getItem("mh-ed-view") || "kanban");
  const [q, setQ] = useState("");
  const [section, setSection] = useState("ALL");
  const [newOpen, setNewOpen] = useState(false);
  const canEdit = canAccess(me.role, "editStories");

  useEffect(() => {
    localStorage.setItem("mh-ed-view", view);
  }, [view]);
  useEffect(() => {
    if (params.get("new") === "1") {
      if (canEdit) setNewOpen(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const { data: stories = [], isLoading } = useQuery({ queryKey: ["stories"], queryFn: async () => (await api.get("/stories")).data });
  const move = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/stories/${id}`, { status })).data,
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ["stories"] });
      const prev = qc.getQueryData(["stories"]);
      qc.setQueryData(["stories"], (old = []) => old.map((s) => (s.id === id ? { ...s, status } : s)));
      return { prev };
    },
    onError: (e, _v, ctx) => { qc.setQueryData(["stories"], ctx.prev); toast.error(errMsg(e)); },
    onSuccess: (s) => toast.success(`Movida a «${stageLabel(s.status)}»`),
    onSettled: () => { qc.invalidateQueries({ queryKey: ["stories"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
  });

  const filtered = useMemo(
    () => stories.filter((s) => (section === "ALL" || s.section === section) && (!q || `${s.title} ${s.authorName}`.toLowerCase().includes(q.toLowerCase()))),
    [stories, section, q]
  );
  const open = (s) => navigate(`/editorial/${s.id}`);

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="03 · Mesa Editorial & Calendario multiplataforma"
        title="Mesa Editorial"
        subtitle="Del pitch a publicado. Arrastra las historias entre etapas o planifica en el calendario."
        actions={canEdit && <Button onClick={() => setNewOpen(true)} data-testid="new-story-button"><Plus className="mr-1.5 h-4 w-4" /> Nuevo borrador</Button>}
      />
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="kanban" data-testid="view-kanban"><Columns3 className="mr-1.5 h-3.5 w-3.5" />Kanban</TabsTrigger>
            <TabsTrigger value="month" data-testid="view-month"><CalendarDays className="mr-1.5 h-3.5 w-3.5" />Mes</TabsTrigger>
            <TabsTrigger value="week" data-testid="view-week"><CalendarRange className="mr-1.5 h-3.5 w-3.5" />Semana</TabsTrigger>
            <TabsTrigger value="table" data-testid="view-table"><Table2 className="mr-1.5 h-3.5 w-3.5" />Tabla</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar por título o autor" className="h-9 w-[220px] pl-8" data-testid="editorial-search-input" />
          </div>
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="editorial-section-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas las secciones</SelectItem>
              {SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {!canEdit && <div className="rounded-md border border-border bg-surface-2 px-4 py-2.5 text-xs text-muted-foreground">Vista de solo lectura para el rol Comercial.</div>}

      {isLoading ? (
        <div className="flex gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-96 w-[296px]" />)}</div>
      ) : stories.length === 0 ? (
        <EmptyState title="La mesa está vacía" text="Crea un borrador o convierte un pitch del Radar." />
      ) : view === "kanban" ? (
        <KanbanBoard
          columns={STORY_STAGES}
          items={filtered}
          getColumn={(s) => s.status}
          canDrag={canEdit}
          onMove={(s, status) => move.mutate({ id: s.id, status })}
          renderCard={(s) => <StoryCard s={s} onClick={() => open(s)} />}
        />
      ) : view === "month" ? (
        <MonthView stories={filtered} open={open} />
      ) : view === "week" ? (
        <WeekView stories={filtered} open={open} />
      ) : (
        <div className="rounded-lg border border-border bg-card" data-testid="editorial-table">
          <Table>
            <TableHeader>
              <TableRow>
                {["Título", "Etapa", "Sección", "Formato", "Autor/a", "Publicación", "Webflow"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id} onClick={() => open(s)} className="cursor-pointer" data-testid="editorial-table-row">
                  <TableCell className="max-w-[360px]"><div className="truncate text-sm font-medium">{s.title}</div></TableCell>
                  <TableCell><StageTag status={s.status} label={stageLabel(s.status)} /></TableCell>
                  <TableCell><SectionTag section={s.section} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatLabel(s.format)}</TableCell>
                  <TableCell className="text-sm">{s.authorName}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(s.publishDate, { day: "2-digit", month: "short", year: "2-digit" })}</TableCell>
                  <TableCell><WebflowBadge status={s.webflowStatus} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <NewStoryDialog open={newOpen} setOpen={setNewOpen} />
    </div>
  );
}
